import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, ClientLogo, Dialog, ModuleHeader, Text } from '@viapeople/components'
import { useState } from 'react'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

interface Organization {
	id: string
	name: string
	logo: string
	cycleCount: number
	createdAt: string
	maintenanceMode: boolean
}

type SearchInput = {
	search?: string
	page?: number
	perPage?: number
	sortBy?: 'name' | 'cycleCount' | 'createdAt' | 'maintenanceMode'
	order?: 'asc' | 'desc'
}

interface SelectionMeta {
	selectAllAcrossPages: boolean
	excludedIds: Set<string>
	includedIds: Set<string>
}

export const MaintenanceModePage = () => {
	const { showToast } = useToast()
	const queryClient = useQueryClient()

	const [selectedOrgs, setSelectedOrgs] = useState<Organization[]>([])
	const [selectionMeta, setSelectionMeta] = useState<SelectionMeta | null>(null)

	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
	const [confirmAction, setConfirmAction] = useState<'enable' | 'disable'>('enable')
	const [singleOrgToToggle, setSingleOrgToToggle] = useState<Organization | null>(null)

	const invalidateQueries = () => {
		queryClient.invalidateQueries({
			predicate: (query) => {
				const key = query.queryKey
				if (Array.isArray(key) && Array.isArray(key[0])) {
					const queryPath = key[0] as string[]
					return queryPath[0] === 'admin' && queryPath[1] === 'maintenanceMode'
				}
				return false
			},
			refetchType: 'all',
		})
	}

	const toggleMutation = useMutation({
		...trpc.admin.maintenanceMode.toggle.mutationOptions(),
		onSuccess: () => {
			showToast(`Maintenance mode ${confirmAction === 'enable' ? 'enabled' : 'disabled'} successfully`)
			setIsConfirmDialogOpen(false)
			setSingleOrgToToggle(null)
			setSelectedOrgs([])
			setSelectionMeta(null)
			invalidateQueries()
		},
		onError: () => {
			showToast(`Failed to ${confirmAction} maintenance mode`, 'error')
		},
	})

	const toggleAllMutation = useMutation({
		...trpc.admin.maintenanceMode.toggleAll.mutationOptions(),
		onSuccess: () => {
			showToast(`Maintenance mode ${confirmAction === 'enable' ? 'enabled' : 'disabled'} for all organizations`)
			setIsConfirmDialogOpen(false)
			setSingleOrgToToggle(null)
			setSelectedOrgs([])
			setSelectionMeta(null)
			invalidateQueries()
		},
		onError: () => {
			showToast(`Failed to ${confirmAction} maintenance mode for all organizations`, 'error')
		},
	})

	const handleSelectionChange = (items: Organization[], meta?: SelectionMeta) => {
		setSelectedOrgs(items)
		setSelectionMeta(meta || null)
	}

	const handleConfirm = () => {
		const enabled = confirmAction === 'enable'

		if (singleOrgToToggle) {
			toggleMutation.mutate({
				organizationIds: [singleOrgToToggle.id],
				enabled,
			})
		} else if (selectionMeta?.selectAllAcrossPages) {
			toggleAllMutation.mutate({
				enabled,
				excludedIds: Array.from(selectionMeta.excludedIds),
			})
		} else if (selectedOrgs.length > 0) {
			toggleMutation.mutate({
				organizationIds: selectedOrgs.map((org) => org.id),
				enabled,
			})
		}
	}

	const handleSingleToggle = (org: Organization, action: 'enable' | 'disable') => {
		setSingleOrgToToggle(org)
		setConfirmAction(action)
		setIsConfirmDialogOpen(true)
	}

	const handleBulkAction = (action: 'enable' | 'disable') => {
		setSingleOrgToToggle(null)
		setConfirmAction(action)
		setIsConfirmDialogOpen(true)
	}

	const getConfirmDescription = () => {
		if (singleOrgToToggle) {
			return singleOrgToToggle.name
		}
		if (selectionMeta?.selectAllAcrossPages) {
			const excludedCount = selectionMeta.excludedIds.size
			if (excludedCount > 0) {
				return `All organizations (except ${excludedCount} excluded)`
			}
			return 'All organizations'
		}
		if (selectedOrgs.length > 20) {
			return `${selectedOrgs.length} organizations selected`
		}
		return selectedOrgs.map((o) => o.name).join(', ')
	}

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		})
	}

	const columns: Column<Organization>[] = [
		{
			field: 'name',
			label: 'Organization Name',
			colSpan: 3,
			render: (org) => (
				<div className='flex items-center gap-3'>
					<ClientLogo url={org.logo} />
					<Text variant='xsmall' weight='semibold'>
						{org.name}
					</Text>
				</div>
			),
		},
		{
			field: 'cycleCount',
			label: '# Cycles',
			colSpan: 1,
			render: (org) => (
				<Text variant='xsmall' className='text-neutral-600'>
					{org.cycleCount}
				</Text>
			),
		},
		{
			field: 'createdAt',
			label: 'Date Created',
			colSpan: 2,
			render: (org) => (
				<Text variant='xsmall' className='text-neutral-600'>
					{formatDate(org.createdAt)}
				</Text>
			),
		},
		{
			field: 'maintenanceMode',
			label: 'Status',
			colSpan: 2,
			render: (org) => (
				<span
					className={`px-2 py-1 rounded text-xs font-medium ${
						org.maintenanceMode ? 'bg-destructive-100 text-destructive-700' : 'bg-success-100 text-success-700'
					}`}>
					{org.maintenanceMode ? 'Maintenance Mode' : 'Active'}
				</span>
			),
		},
		{
			field: 'id',
			label: 'Actions',
			colSpan: 2,
			sortable: false,
			render: (org) => (
				<Button
					size='small'
					variant={org.maintenanceMode ? 'primary' : 'secondary'}
					onClick={(e) => {
						e.stopPropagation()
						handleSingleToggle(org, org.maintenanceMode ? 'disable' : 'enable')
					}}>
					{org.maintenanceMode ? 'Disable Maintenance' : 'Enable Maintenance'}
				</Button>
			),
		},
	]

	const showBulkActions = selectedOrgs.length > 0 || selectionMeta?.selectAllAcrossPages

	const getSelectionText = () => {
		if (selectionMeta?.selectAllAcrossPages) {
			const excludedCount = selectionMeta.excludedIds.size
			if (excludedCount > 0) {
				return `All organizations selected (${excludedCount} excluded)`
			}
			return 'All organizations selected'
		}
		return `${selectedOrgs.length} organization(s) selected`
	}

	return (
		<main className='w-full p-6 flex flex-col gap-6'>
			<ModuleHeader
				breadcrumb={[{ label: 'Global Settings', to: '/settings' }, { label: 'Maintenance Mode' }]}
				title='Maintenance Mode'
			/>

			<Text variant='small' className='text-neutral-600 -mt-4 text-left'>
				Enable maintenance mode to temporarily block client access to specific organizations. Users will see a
				maintenance message until the mode is disabled.
			</Text>

			{showBulkActions && (
				<div className='flex items-center gap-4 border border-neutral-900 rounded-lg p-4 w-fit'>
					<Text variant='small' weight='semibold' className='text-neutral-600'>
						{getSelectionText()}
					</Text>
					<div className='flex gap-2'>
						<Button size='small' variant='destructive' onClick={() => handleBulkAction('enable')}>
							Enable Maintenance Mode
						</Button>
						<Button variant='primary' size='small' onClick={() => handleBulkAction('disable')}>
							Disable Maintenance Mode
						</Button>
					</div>
				</div>
			)}

			<GenericTable<Organization, SearchInput>
				columns={columns}
				selectable
				selectAllAcrossPages
				onSelectionChange={handleSelectionChange}
				queryOptionsGenerator={(options) => trpc.admin.maintenanceMode.search.queryOptions(options)}
				idField='id'
				emptyState={{
					title: 'No organizations',
					description: 'No organizations found in the system.',
				}}
			/>

			<Dialog
				isOpen={isConfirmDialogOpen}
				onClose={() => {
					setIsConfirmDialogOpen(false)
					setSingleOrgToToggle(null)
				}}
				onConfirm={handleConfirm}
				title={`${confirmAction === 'enable' ? 'Enable' : 'Disable'} Maintenance Mode`}
				message={`Are you sure you want to ${confirmAction} maintenance mode for: ${getConfirmDescription()}?${
					confirmAction === 'enable'
						? ' Users will see a maintenance message and will not be able to access the application.'
						: ' Users will regain access to the application.'
				}`}
				type={confirmAction === 'enable' ? 'delete' : 'confirm'}
				confirmLabel={confirmAction === 'enable' ? 'Enable Maintenance' : 'Disable Maintenance'}
				isConfirmLoading={toggleMutation.isPending || toggleAllMutation.isPending}
			/>
		</main>
	)
}
